import type { SemanticTone } from "@/shared/ui/core/types";

/**
 * Chuẩn hóa chuỗi để so khớp từ khóa trạng thái / nhãn (chữ thường, bỏ dấu tiếng Việt, bỏ khoảng trắng/kí tự phân cách).
 */
export function normalizeToneKey(str: string | null | undefined): string {
  if (!str) return "";
  return str
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/[\s_-]+/g, "");
}

/**
 * Quy tắc: Màu của tất cả, tổng là màu trung tính (neutral).
 */
export function isTotalToneKey(key: string): boolean {
  return (
    key === "all" ||
    key === "total" ||
    key === "tatca" ||
    key === "tong" ||
    key === "tongso" ||
    key === "tongcong" ||
    key === "toanbo" ||
    key.startsWith("tong") ||
    key.startsWith("tatca")
  );
}

/**
 * Đảm bảo card Tổng / Tất cả luôn luôn nằm ở vị trí đầu tiên (index 0).
 */
export function orderKpisWithTotalFirst<T extends { id?: string; label?: string }>(
  items: readonly T[],
): T[] {
  const totalIdx = items.findIndex((item) => {
    const idKey = normalizeToneKey(item.id);
    const labelKey = normalizeToneKey(item.label);
    return isTotalToneKey(idKey) || isTotalToneKey(labelKey);
  });
  if (totalIdx <= 0) return [...items];
  const totalItem = items[totalIdx];
  if (!totalItem) return [...items];
  const rest = items.filter((_, i) => i !== totalIdx);
  return [totalItem, ...rest];
}

export type CanonicalKpiTone = "neutral" | "info" | "success" | "danger" | "draft";

export function toCanonicalKpiTone(tone: SemanticTone | undefined): CanonicalKpiTone {
  if (!tone || tone === "neutral") return "neutral";
  if (tone === "draft") return "draft";
  if (tone === "info") return "info";
  if (tone === "danger" || tone === "hold") return "danger";
  if (tone === "success" || tone === "primary" || tone === "accent") return "success";
  // warning (màu cam) tuyệt đối bị cấm -> chuyển thành info
  return "info";
}

/**
 * Quy tắc cốt lõi:
 * 1. Tuyệt đối không có 2 thẻ KPI nào trùng màu trên cùng 1 thanh / list.
 * 2. Tuyệt đối không có thẻ màu cam (warning) trên toàn bộ các list.
 */
export function ensureUniqueKpiTones<
  T extends { id?: string; label?: string; tone?: SemanticTone }
>(items: readonly T[]): T[] {
  const ordered = orderKpisWithTotalFirst(items);
  const usedTones = new Set<CanonicalKpiTone>();
  const availableFallbackTones: CanonicalKpiTone[] = [
    "info",
    "danger",
    "success",
    "draft",
  ];

  return ordered.map((item, idx) => {
    const rawTone = resolveKpiTone(item);
    const idKey = normalizeToneKey(item.id);
    const labelKey = normalizeToneKey(item.label);

    let canonical = toCanonicalKpiTone(rawTone);

    if (idx === 0 && (isTotalToneKey(idKey) || isTotalToneKey(labelKey))) {
      canonical = "neutral";
    } else if (canonical === "neutral") {
      canonical = "draft";
    }

    // Không cho phép trùng màu với thẻ đã xuất hiện trước đó trên thanh
    if (usedTones.has(canonical)) {
      const fallback = availableFallbackTones.find((t) => !usedTones.has(t));
      if (fallback) {
        canonical = fallback;
      }
    }

    usedTones.add(canonical);
    return canonical === item.tone ? item : { ...item, tone: canonical };
  });
}

/**
 * Quy tắc: Màu status đã hủy là màu nóng (danger).
 */
export function isCancelledToneKey(key: string): boolean {
  return (
    key === "cancel" ||
    key === "cancelled" ||
    key === "canceled" ||
    key === "void" ||
    key === "reject" ||
    key === "rejected" ||
    key === "huy" ||
    key === "dahuy" ||
    key === "bihuy" ||
    key === "tuchoi" ||
    key.includes("dahuy") ||
    key.includes("cancelled") ||
    key === "huybo"
  );
}

/**
 * Quy tắc: Màu chờ, nháp,... là màu xám (neutral).
 */
export function isPendingDraftToneKey(key: string): boolean {
  // "Chờ cấp phát" / "Đã cấp phát" — tone riêng (warning / accent), không gộp draft.
  if (key.includes("capphat")) return false;
  // "Chờ duyệt" / "Pending approval" — tone riêng (info), không gộp draft để tránh trùng màu với Nháp.
  if (
    key.includes("choduyet") ||
    key.includes("pendingapproval") ||
    key.includes("submitted") ||
    key.includes("chuaduyet") ||
    key.includes("chopheduyet")
  ) {
    return false;
  }
  return (
    key === "pending" ||
    key === "draft" ||
    key === "waiting" ||
    key === "nhap" ||
    key === "tamluu" ||
    key === "cho" ||
    key.startsWith("cho") ||
    key.includes("chokiem") ||
    key.includes("choxuly") ||
    key.includes("chonhan") ||
    key.includes("chosx") ||
    key.includes("choxuat") ||
    key.includes("chogiao") ||
    key.includes("chokehoach")
  );
}

/**
 * Bộ quy chuẩn màu cho KpiCard và các card tổng quan ở mobile.
 * Tuân thủ tuyệt đối:
 * 1. Tất cả, tổng -> neutral (màu trung tính)
 * 2. Đã hủy, hủy, void, rejected -> danger (màu nóng)
 * 3. Chờ, nháp, pending, draft -> neutral (màu xám)
 * 4. Các trạng thái khác suy diễn theo chuẩn Design System (success / accent / warning / danger / neutral).
 */
export function resolveKpiTone(spec: {
  id?: string;
  label?: string;
  tone?: SemanticTone;
}): SemanticTone {
  const idKey = normalizeToneKey(spec.id);
  const labelKey = normalizeToneKey(spec.label);

  // 1. Tất cả, tổng -> màu trung tính đậm (neutral)
  if (isTotalToneKey(idKey) || isTotalToneKey(labelKey)) {
    return "neutral";
  }

  // 2. Status đã hủy -> màu nóng (danger)
  if (isCancelledToneKey(idKey) || isCancelledToneKey(labelKey)) {
    return "danger";
  }

  const combined = `${idKey} ${labelKey}`;

  // Batch MES — đã cấp phát -> xanh lá tích cực (success), tạm giữ -> đỏ (danger), chờ cấp phát -> xám (draft)
  if (combined.includes("chocapphat")) {
    return "draft";
  }
  if (
    idKey === "issued" ||
    (combined.includes("dacapphat") && !combined.includes("chocapphat"))
  ) {
    return "success";
  }
  if (
    idKey === "hold" ||
    labelKey.includes("tamgiu") ||
    labelKey.includes("lenhgiu") ||
    labelKey.includes("danggiu")
  ) {
    return "danger";
  }

  // Chờ kế hoạch -> draft (xám nhạt)
  if (combined.includes("chokehoach")) {
    return "draft";
  }

  // Chờ duyệt / submitted / pendingapproval / chopheduyet -> BẮT BUỘC xanh dương (info) trên toàn bộ hệ thống
  if (
    combined.includes("choduyet") ||
    combined.includes("pendingapproval") ||
    combined.includes("submitted") ||
    combined.includes("chuaduyet") ||
    combined.includes("chopheduyet")
  ) {
    return "info";
  }

  // FIN «Thu một phần» (Partial) → info — đồng bộ KPI ModuleInsights (khác MES «Cấp một phần» = danger).
  if (labelKey.includes("thumotphan")) {
    return "info";
  }
  if (labelKey.includes("capmotphan")) {
    return "danger";
  }

  // 3. Nếu caller đã chỉ định tone hợp lệ (ngoại trừ "primary" thường bị gán nhầm cho "total"):
  if (spec.tone && spec.tone !== "primary") {
    // Chuyển accent -> success, hold -> danger
    if (spec.tone === "accent") {
      return "success";
    }
    if (spec.tone === "hold") {
      return "danger";
    }
    // Nếu caller truyền "neutral" mà KHÔNG phải Tổng -> chuyển thành "draft" (nền xám nhạt)
    if (spec.tone === "neutral") {
      return "draft";
    }
    // Tuyệt đối không có thẻ màu cam (warning) trên toàn bộ các list -> chuyển sang info
    if (spec.tone === "warning") {
      return "info";
    }
    return spec.tone;
  }

  // Chờ khác, nháp, pending, draft -> màu nền nhạt của màu xám (draft)
  if (isPendingDraftToneKey(idKey) || isPendingDraftToneKey(labelKey)) {
    return "draft";
  }

  // 5. Đã lên kế hoạch / scheduled (Demand) -> info — đồng bộ KPI filter «Đã lên kế hoạch»
  if (
    combined.includes("planned") ||
    combined.includes("dalenkehoach") ||
    combined.includes("dakehoach") ||
    combined.includes("lenkehoach") ||
    combined.includes("dakh") ||
    combined.includes("scheduled")
  ) {
    return "info";
  }

  // 6. Đã phát hành (MO) -> info — cùng nhóm KPI «Đang thực hiện» với InProgress
  if (combined.includes("released") || combined.includes("daphathanh")) {
    return "info";
  }

  // 7. Đang chạy, đang SX, in progress -> xanh dương (info)
  if (
    combined.includes("dangchay") ||
    combined.includes("dangsx") ||
    combined.includes("dangsanxuat") ||
    combined.includes("inprogress") ||
    combined.includes("running") ||
    combined.includes("inproduction") ||
    combined.includes("wip") ||
    combined.includes("dangxuly") ||
    combined.includes("processing")
  ) {
    return "info";
  }
  if (
    combined.includes("dangtat") ||
    combined.includes("khonghoatdong") ||
    combined.includes("inactive") ||
    combined.includes("boqua") ||
    combined.includes("skipped")
  ) {
    return "draft";
  }
  // ENG version Obsolete / Hết hiệu lực — đồng bộ badge danger
  if (
    combined.includes("obsolete") ||
    combined.includes("hethieuluc") ||
    labelKey.includes("ngung")
  ) {
    return "danger";
  }

  if (
    combined.includes("quahan") ||
    combined.includes("overdue") ||
    combined.includes("blocked") ||
    combined.includes("loi") ||
    (combined.includes("hong") && !combined.includes("khong")) ||
    combined.includes("suco") ||
    combined.includes("critical") ||
    combined.includes("unread") ||
    combined.includes("chuadoc")
  ) {
    return "danger";
  }

  // Tuyệt đối không dùng màu cam (warning) -> chuyển cảnh báo / tạm dừng / gần hạn sang đỏ (danger)
  if (
    combined.includes("tamdung") ||
    combined.includes("paused") ||
    combined.includes("ganhan") ||
    combined.includes("saphethan") ||
    combined.includes("atrisk") ||
    combined.includes("canhbao")
  ) {
    return "danger";
  }

  if (
    combined.includes("dangmo") ||
    combined.includes("open")
  ) {
    return "info";
  }

  if (
    combined.includes("hoanthanh") ||
    combined.includes("hoantat") ||
    combined.includes("dahoantat") ||
    combined.includes("thanhcong") ||
    combined.includes("daketthuc") ||
    combined.includes("xong") ||
    combined.includes("completed") ||
    combined.includes("done") ||
    combined.includes("active") ||
    combined.includes("danghoatdong") ||
    (combined.includes("hoatdong") && !combined.includes("khonghoatdong")) ||
    combined.includes("dangbat") ||
    combined.includes("approved") ||
    combined.includes("confirmed") ||
    combined.includes("danhap") ||
    combined.includes("closed") ||
    combined.includes("dadong") ||
    combined.includes("dadoc") ||
    combined.includes("danha") ||
    combined.includes("dago")
  ) {
    return "success";
  }

  if (spec.tone === "primary") {
    return "success";
  }
  return "draft";
}

/**
 * Style chuẩn cho các tile/card tổng quan ở mobile (như FieldTodayKpiStrip, Mobile overview).
 */
export const MOBILE_KPI_TILE_STYLES: Record<
  SemanticTone,
  { badge: string; value: string }
> = {
  neutral: {
    badge: "border-slate-200 bg-white text-slate-700 shadow-xs",
    value: "text-slate-900",
  },
  primary: {
    badge: "border-emerald-700 bg-primary text-white shadow-xs",
    value: "text-white",
  },
  accent: {
    badge: "border-emerald-700 bg-primary text-white shadow-xs",
    value: "text-white",
  },
  success: {
    badge: "border-emerald-700 bg-primary text-white shadow-xs",
    value: "text-white",
  },
  warning: {
    badge: "border-kpi-warning-border bg-kpi-warning-bg text-kpi-warning-fg shadow-xs",
    value: "text-kpi-warning-fg",
  },
  danger: {
    badge: "border-kpi-danger-border bg-kpi-danger-bg text-kpi-danger-fg shadow-xs",
    value: "text-kpi-danger-fg",
  },
  info: {
    badge: "border-kpi-info-border bg-kpi-info-bg text-kpi-info-fg shadow-xs",
    value: "text-kpi-info-fg",
  },
  hold: {
    badge: "border-kpi-danger-border bg-kpi-danger-bg text-kpi-danger-fg shadow-xs",
    value: "text-kpi-danger-fg",
  },
  draft: {
    badge: "border-kpi-draft-border bg-kpi-draft-bg text-kpi-draft-fg shadow-xs",
    value: "text-kpi-draft-fg",
  },
};

